import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import HomeText from './HomeText';
import StepProcess from './StepProcess';
import MobileStepProcess from './MobileStepProcess';
import VideoScreen from './VideoScreen';
import BragBar from './BragBar';
import ServiceInfos from './ServiceInfos';
import ContactUs from './ContactUs';
import PageFooter from './PageFooter';
import { initScrollAnimations, resetScrollAnimations } from '../utils/scrollAnimations';
import './BlurryOrbs.css';

function Home() {
  const [showFloatingLogo, setShowFloatingLogo] = useState(false);
  const [orbOpacity, setOrbOpacity] = useState(1);
  const contactUsRef = useRef(null);
  const ticking = useRef(false);
  const cleanupAnimations = useRef(null);

  // Throttled scroll handler using requestAnimationFrame
  const updateScrollState = useCallback(() => {
    const scrollPosition = window.scrollY;
    
    // Update floating logo visibility
    setShowFloatingLogo(scrollPosition > 200);
    
    // Optimize orb fade calculation - only update if significant change
    const maxScroll = 800;
    const newOpacity = Math.max(0, 1 - (scrollPosition / maxScroll));
    
    // Only update if opacity change is significant (avoid unnecessary re-renders)
    setOrbOpacity(prevOpacity => {
      const diff = Math.abs(prevOpacity - newOpacity);
      return diff > 0.05 ? newOpacity : prevOpacity;
    });
    
    ticking.current = false;
  }, []);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(updateScrollState);
      ticking.current = true;
    }
  }, [updateScrollState]);

  useEffect(() => {
    // Reset scroll position when component mounts (coming from other routes)
    window.scrollTo(0, 0);
    
    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initialize scroll animations with a slight delay to ensure all components are rendered
    const timer = setTimeout(() => {
      cleanupAnimations.current = initScrollAnimations();
    }, 150);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
      // Clean up animations when component unmounts
      if (cleanupAnimations.current) {
        cleanupAnimations.current();
      }
    };
  }, [handleScroll]);

  const scrollToApply = useCallback(() => {
    contactUsRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'center'
    });
  }, []);

  const openBookingCall = useCallback(() => {
            window.open('https://calendly.com/songaudit', '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <>
      {/* Blurry Orb Animations - Only render if visible */}
      {orbOpacity > 0.01 && (
        <div className="blurry-orbs" style={{ 
          opacity: orbOpacity,
          willChange: orbOpacity > 0.05 ? 'opacity' : 'auto'
        }}>
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
          <div className="orb orb-4"></div>
        </div>
      )}

      {/* Floating Logo - Only render when visible */}
      {showFloatingLogo && (
        <div className="floating-logo">
          <img 
            src="/img/audit_icon.webp" 
            alt="Song Audit Logo" 
            className="floating-logo-icon"
            loading="eager"
          />
        </div>
      )}

      <header className="app-header">
        <div className="header-logo">
          <img 
            src="/img/audit_icon.webp" 
            alt="Song Audit Logo" 
            className="logo-icon"
            loading="eager"
          />
        </div>
        
        <div className="header-icons">
          <a href="https://instagram.com/songaudit" target="_blank" rel="noopener noreferrer" className="header-icon-container">
            <img src="/img/social.svg" alt="Instagram" className="header-icon" loading="lazy" />
          </a>
          <a href="https://calendly.com/songaudit" target="_blank" rel="noopener noreferrer" className="header-icon-container">
            <img src="/img/video.svg" alt="Book a Call" className="header-icon" loading="lazy" />
          </a>
          <a href="https://www.upwork.com/freelancers/~01d79e2dddd92177f9" target="_blank" rel="noopener noreferrer" className="header-icon-container">
            <img src="/img/upwork.svg" alt="LinkedIn" className="header-icon" loading="lazy" />
          </a>
        </div>
        
        <div className="header-apply">
          <Link to="/access" className="members-area-text">Audit Dashboard</Link>
          <button className="apply-button" onClick={openBookingCall}>Start Free Audit</button>
        </div>
      </header>

      <main className="main-content">
        <div className="main-logo">
          <img 
            src="/img/honest_logo_high.webp" 
            alt="Song Audit - Music Catalog Analysis Platform" 
            className="main-logo-image"
            loading="eager"
            decoding="async"
          />
        </div>
        
        <HomeText />
        <BragBar />
        <VideoScreen />
        <StepProcess />
        <MobileStepProcess />
        <ServiceInfos />
        <div ref={contactUsRef}>
          <ContactUs />
        </div>
      </main>
      
      <PageFooter />
    </>
  );
}

export default Home; 